<template>
  <div>
    <md-dialog
      :md-active="state.popupOpen"
      :md-close-on-esc="false"
      :md-click-outside-to-close="false"
      class="popup-notifications-dialog"
    >
      <md-dialog-content class="scroll-content md-scrollbar">
        <div class="slick-container">
          <slick
            ref="slick"
            :options="slickOptions" 
            @beforeChange="beforeSlideChange"
          >
            <div
              v-for="notification in orderedNotifications"
              :key="notification._id"
              class="notification-slide"
            >
              <md-card>
                <img
                  v-if="notification.media && notification.media.type === 'IMAGE'"
                  :src="notification.media.url"
                  class="notification-media"
                >
                <md-button class="md-icon-button close-button" @click="closePopup">
                  <md-icon>close</md-icon>
                </md-button>
              </md-card>
              <h3 class="notification-title gray-5 color">{{ notification.title }}</h3>
              <p class="notification-message gray-5 color">
                {{ notification.message }}
              </p>
            </div>
          </slick>
        </div>
      </md-dialog-content>
      <md-dialog-actions v-if="currentNotification && currentNotification.actions && currentNotification.actions.length">
        <md-button
          v-for="action in currentNotification.actions"
          :key="action._id"
          :href="action.type === 'URL' ? action.metadata.url : undefined"
          :target="action.type === 'URL' ? '_blank' : undefined"
          :disabled="state.performingAction"
          class="md-primary md-raised"
          @click="doAction(action)"
        >
          {{ action.label }}
        </md-button>
      </md-dialog-actions>
      <md-dialog-actions v-else>
        <md-button class="md-primary md-raised" @click="nextSlide">
          <span v-if="state.currentSlide === state.notifications.length - 1">Go to my cases</span>
          <span v-else-if="state.currentSlide === 0">Show me more</span>
          <span v-else>Next</span>
          <md-icon>arrow_right_alt</md-icon>
        </md-button>
      </md-dialog-actions>
      <div class="remind-me-container" @click="remindMe()">
        <span class="remind-me-button">Remind Me at Next Login</span>
      </div>
      <transition name="fade">
        <lottie v-show="state.playingAnimation" :options="lottieOptions" class="confetti-animation" @animCreated="onAnimationCreated" />
      </transition>
    </md-dialog>
  </div>
</template>

<script>
import Slick from 'vue-slick';
import Lottie from 'vue-lottie';
import orderBy from 'lodash/orderBy';

import confettiAnimation from './confetti-animation.json';

export default {
  name: 'popup-notifications',
  components: {
    Slick,
    Lottie,
  },
  props: {
    notifications: {
      type: Array,
      default: () => [],
    },
    getUserPref: {
      type: Function,
      required: true,
    },
    setUserPref: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      state: {
        popupOpen: false,
        currentSlide: 0,
        notifications: [],
        playingAnimation: false,
        performingAction: false,
      },
      slickOptions: {
        slidesToShow: 1,
        focusOnSelect: false,
        draggable: false,
        arrows: false,
        initialSlide: 0,
        dots: true,
      },
      lottieOptions: {
        animationData: confettiAnimation,
        autoplay: false,
      }
    };
  },
  computed: {
    orderedNotifications() {
      return orderBy(this.state.notifications, ['date', '_id']);
    },
    currentNotification() {
      return this.orderedNotifications[this.state.currentSlide];
    },
  },
  watch: {
    notifications() {
      if (this.getUserPref('disablePopupNotifications')) {
        return;
      }
      if (this.notifications.length && !this.state.notifications.length) {
        this.state.popupOpen = !!this.notifications.length;
        this.state.notifications = this.notifications;
      }
    },
    'state.notifications'() {
      this.state.notifications.forEach(notification => this.dismiss(notification._id));
    },
    currentNotification(newVal) {
      if (newVal && newVal.animation) {
        this.playAnimation();
      }
    },
  },
  mounted() {
    if (this.getUserPref('disablePopupNotifications')) {
      return;
    }
    this.state.popupOpen = !!this.notifications.length;
    if (this.currentNotification && this.currentNotification.animation) {
      this.playAnimation();
    }
  },
  methods: {
    closePopup() {
      this.state.popupOpen = false;
    },
    remindMe() {
      this.closePopup();
      this.setUserPref('disablePopupNotifications', true);
      this.state.notifications.forEach(notification => this.unread(notification._id));
    },
    beforeSlideChange(event, slick, currentSlide, nextSlide) {
      this.state.currentSlide = nextSlide;
    },
    nextSlide() {
      const currentSlide = this.$refs.slick.currentSlide();
      if (currentSlide < this.state.notifications.length - 1) {
        this.$refs.slick.next();
      } else {
        this.closePopup();
      }
    },
    onAnimationCreated(anim) {
      this.anim = anim;
      anim.setSpeed(2);
      if (this.currentNotification && this.currentNotification.animation) {
        this.playAnimation();
      }
    },
    playAnimation() {
      if (!this.anim) {
        return;
      }
      this.state.playingAnimation = true;
      this.anim.play();
      setTimeout(() => {
        this.state.playingAnimation = false;
      }, 10000);
    },
    dismiss(notificationId) {
      this.$emit('dismiss', notificationId);
    },
    unread(notificationId) {
      this.$emit('unread', notificationId);
    },
    async doAction(action) {
      if (action.type === 'EVENT_HUB') {
        this.state.performingAction = true;
        this.$emit('doAction', action, this.currentNotification, () => {
          this.state.performingAction = false;
          this.nextSlide();
        });
      } else {
        this.$emit('doAction', action, this.currentNotification)
        this.nextSlide();
      }
      if (action.type === 'DISMISS') {
        this.$analytics.event('Notifications', 'notification_dismiss', this.currentNotification._id);
      } else {
        this.trackNotificationClick();
      }
    },
    trackNotificationClick() {
      this.$analytics.event('Notifications', 'notification_go_to', this.currentNotification._id);
    }
  },
};
</script>

<style scoped>
.md-dialog {
  max-height: 700px;
  border: none;
  overflow: visible;
}
.md-dialog-content {
  overflow: auto;
  overflow-x: hidden;
  margin: 0 !important;
  padding-left: 24px !important;
  padding-right: 24px !important;
  background-color: white;
}
.slick-container {
  max-width: 600px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.notification-slide {
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.notification-media {
  height: 400px;
  margin: auto;
}
.close-button {
  position: absolute;
  top: 0;
  right: 0;
}
.notification-title {
  font-weight: bold;
  font-size: 24px;
}
.notification-message {
  font-size: 18px;
  line-height: 24px;
}

.slick-container >>> .slick-dots li.slick-active button:before {
  color: #3f83c5;
}

.md-dialog-actions {
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  padding: 0 !important;
}

.md-dialog-actions .md-button {
  flex: 1;
  border-radius: 0;
  height: 65px;
}

.md-dialog-actions .md-button+.md-button {
  margin-left: 2px;
}

.remind-me-container {
  position: absolute;
  bottom: -30px;
  left: 0;
  right: 0;
  text-align: center;
}

.remind-me-button {
  color: #fff;
  cursor: pointer;
  text-decoration: underline;
}

.confetti-animation {
  position: fixed;
  transform: scaleX(200%);
  left: 0;
  right: 0;
  top: 0;
  z-index: -1;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-active {
  opacity: 0;
}
</style>
